import { modelState } from "@/utils/modelState";
import { toDateTimeString } from "@/utils/utils";
import { ReservationState } from "./types";
import { maxBy } from "lodash";

export const ReservationDepartureTransferMode = Symbol("ReservationDepartureTransferMode");
export interface ReservationDepartureTransferMode {
    [ReservationDepartureTransferMode]: boolean;
}

export const ReservationFlightDepartureTransferMode = Symbol("ReservationFlightDepartureTransferMode");
export interface ReservationFlightDepartureTransferMode extends ReservationDepartureTransferMode {
    [ReservationFlightDepartureTransferMode]: boolean;
    FlightNumber: modelState<string>;
    DepartureTime: modelState<Date>;
    Details: modelState<string>;
}

export function isReservationFlightDepartureTransferMode(val: ReservationDepartureTransferMode): val is ReservationFlightDepartureTransferMode {
    return val && (val as ReservationFlightDepartureTransferMode)[ReservationFlightDepartureTransferMode] === true;
}

export const ReservationOtherDepartureTransferMode = Symbol("ReservationOtherDepartureTransferMode");
export interface ReservationOtherDepartureTransferMode extends ReservationDepartureTransferMode {
    [ReservationOtherDepartureTransferMode]: boolean;
    Details: modelState<string>;
}

export function isReservationOtherDepartureTransferMode(val: ReservationDepartureTransferMode): val is ReservationOtherDepartureTransferMode {
    return val && (val as ReservationOtherDepartureTransferMode)[ReservationOtherDepartureTransferMode] === true;
}

export interface ReservationDepartureTransferModeDto {
    $type: string;
    value: ReservationDepartureTransferModeValueDto;
}

export interface ReservationDepartureTransferModeValueDto {
    //
}

export interface ReservationFlightDepartureTransferModeValueDto extends ReservationDepartureTransferModeValueDto {
    flightNumber: string;
    departureTime: string;
    details: string;
}

export interface ReservationOtherDepartureTransferModeValueDto extends ReservationDepartureTransferModeValueDto {
    details: string;
}

export function serializeReservationDepartureTransferMode(action: ReservationDepartureTransferMode): ReservationDepartureTransferModeDto {
    if (!action) {
        return null;
    }

    if (isReservationFlightDepartureTransferMode(action)) {
        return {
            $type: "Flight",
            value: {
                FlightNumber: action.FlightNumber.value,
                DepartureTime: toDateTimeString(action.DepartureTime.value),
                Details: action.Details.value
            }
        };
    }

    if (isReservationOtherDepartureTransferMode(action)) {
        return {
            $type: "Other",
            value: {
                Details: action.Details.value
            }
        };
    }

    throw new Error("Invalid mode type");
}

export function deserializeReservationDepartureTransferMode(dto: ReservationDepartureTransferModeDto): ReservationDepartureTransferMode {
    if (!dto) {
        return null;
    }

    switch (dto.$type) {
        case "Flight":
            const flightValue = dto.value as ReservationFlightDepartureTransferModeValueDto;

            const flightMode = createReservationFlightDepartureTransferMode(null);

            flightMode.FlightNumber.setValue(flightValue.flightNumber);
            flightMode.FlightNumber.commit();

            flightMode.DepartureTime.setValue(new Date(flightValue.departureTime));
            flightMode.DepartureTime.commit();

            flightMode.Details.setValue(flightValue.details);
            flightMode.Details.commit();

            return flightMode;
        case "Other":
            const otherValue = dto.value as ReservationOtherDepartureTransferModeValueDto;

            const otherMode = createReservationOtherDepartureTransferMode();
            otherMode.Details.setValue(otherValue.details);

            return otherMode;
        default:
            throw new Error("Invalid mode type");
    }
}

export function getReservationDepartureTransferModeName(mode: ReservationDepartureTransferMode | null): string {
    if (!mode) {
        return "None";
    }

    if (isReservationFlightDepartureTransferMode(mode)) {
        return "Flight";
    }

    if (isReservationOtherDepartureTransferMode(mode)) {
        return "Other";
    }

    throw new Error("Invalid mode type");
}

export function createReservationFlightDepartureTransferMode(departureTime: Date | null): ReservationFlightDepartureTransferMode {
    const mode: ReservationFlightDepartureTransferMode = {
        [ReservationDepartureTransferMode]: true,
        [ReservationFlightDepartureTransferMode]: true,
        FlightNumber: modelState(""),
        DepartureTime: modelState(departureTime ?? new Date()),
        Details: modelState("")
    };

    return mode;
};

export function createReservationOtherDepartureTransferMode(): ReservationOtherDepartureTransferMode {
    const mode: ReservationOtherDepartureTransferMode = {
        [ReservationDepartureTransferMode]: true,
        [ReservationOtherDepartureTransferMode]: true,
        Details: modelState("")
    };

    return mode;
}

export function createReservationDepartureTransferMode(name: string, reservationState: ReservationState): ReservationDepartureTransferMode {

    const roomBookings = reservationState.RoomBookings.toArray();

    let latestRoom: Date | null = null;;

    if (roomBookings.length > 0) {
        latestRoom = maxBy(roomBookings, r => r.CheckOut.value.getTime()).CheckOut.value;
    }

    switch (name) {
        case "None":
            return null;
        case "Flight":
            return createReservationFlightDepartureTransferMode(latestRoom);
        case "Other":
            return createReservationOtherDepartureTransferMode();
        default:
            throw new Error("Invalid mode name");
    }
}
